import { db, storage } from '../firebase'
import {
  collection,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  doc,
  serverTimestamp,
  onSnapshot,
  Timestamp,
  setDoc,
  limit,
} from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'

// Maximum number of conversations to fetch per query
const CONVERSATION_QUERY_LIMIT = 50

export interface Message {
  id: string
  senderId: string
  receiverId: string
  conversationId: string
  text?: string
  timestamp: any
  read: boolean
  attachmentUrl?: string
  attachmentType?: 'image' | 'pdf' | 'audio' | 'video' | 'doc'
  attachmentName?: string
}

export interface Conversation {
  id: string
  teacherId: string
  teacherName: string
  teacherAvatar?: string
  studentId: string
  studentName: string
  studentAvatar?: string
  lastMessage: string
  lastMessageTimestamp: any
  lastMessageSenderId: string
  unread?: boolean
  instrument?: string
}

// Create a new conversation between teacher and student
export const createConversation = async (
  teacherId: string,
  teacherName: string,
  teacherAvatar: string,
  studentId: string,
  studentName: string,
  studentAvatar: string,
  instrument: string,
): Promise<string> => {
  try {
    // Check if conversation already exists between these two users
    const existingConversation = await getConversationBetweenUsers(
      teacherId,
      studentId,
    )
    if (existingConversation) {
      return existingConversation.id
    }
    // Create new conversation
    const conversationRef = collection(db, 'conversations')
    const newConversation = await addDoc(conversationRef, {
      teacherId,
      teacherName,
      teacherAvatar,
      studentId,
      studentName,
      studentAvatar,
      instrument,
      lastMessage: '',
      lastMessageTimestamp: serverTimestamp(),
      lastMessageSenderId: '',
      unread: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return newConversation.id
  } catch (error) {
    console.error('Error creating conversation:', error)
    throw error
  }
}

// Get a specific conversation by ID
export const getConversation = async (
  conversationId: string,
): Promise<Conversation | null> => {
  try {
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationDoc = await getDoc(conversationRef)
    if (!conversationDoc.exists()) {
      return null
    }
    return {
      id: conversationDoc.id,
      ...conversationDoc.data(),
    } as Conversation
  } catch (error) {
    console.error('Error getting conversation:', error)
    throw error
  }
}

// Get a specific conversation between teacher and student
export const getConversationBetweenUsers = async (
  teacherId: string,
  studentId: string,
): Promise<Conversation | null> => {
  try {
    const conversationsRef = collection(db, 'conversations')
    const q = query(
      conversationsRef,
      where('teacherId', '==', teacherId),
      where('studentId', '==', studentId),
    )
    const querySnapshot = await getDocs(q)
    if (querySnapshot.empty) {
      return null
    }
    const conversationDoc = querySnapshot.docs[0]
    return {
      id: conversationDoc.id,
      ...conversationDoc.data(),
    } as Conversation
  } catch (error) {
    console.error('Error getting conversation:', error)
    throw error
  }
}

// Send a message in a conversation
export const sendMessage = async (
  conversationId: string,
  senderId: string,
  receiverId: string,
  text?: string,
  attachment?: {
    file: File
    type: 'image' | 'pdf' | 'audio' | 'video' | 'doc'
  },
): Promise<string> => {
  try {
    // Validate required parameters
    if (!conversationId) {
      console.error('sendMessage: Missing conversationId')
      throw new Error('Missing conversation ID')
    }
    if (!senderId) {
      console.error('sendMessage: Missing senderId')
      throw new Error('Missing sender ID')
    }
    if (!receiverId) {
      console.error('sendMessage: Missing receiverId')
      throw new Error('Missing receiver ID')
    }
    // Verify conversation exists before sending message
    const conversationRef = doc(db, 'conversations', conversationId)
    const conversationDoc = await getDoc(conversationRef)
    if (!conversationDoc.exists()) {
      console.error(`Conversation with ID ${conversationId} does not exist`)
      throw new Error('Conversation not found')
    }
    const conversationData = conversationDoc.data()
    console.log('Sending message to existing conversation:', {
      conversationId,
      teacherId: conversationData.teacherId,
      studentId: conversationData.studentId,
      senderId,
      receiverId,
    })
    // Verify sender is either the teacher or student in this conversation
    if (
      senderId !== conversationData.teacherId &&
      senderId !== conversationData.studentId
    ) {
      console.error(
        `User ${senderId} is not authorized to send messages in conversation ${conversationId}`,
      )
      throw new Error('Unauthorized to send messages in this conversation')
    }
    let attachmentUrl = undefined
    let attachmentType = undefined
    let attachmentName = undefined
    // Upload attachment if provided
    if (attachment) {
      try {
        console.log('Uploading attachment:', {
          fileName: attachment.file.name,
          fileType: attachment.file.type,
          fileSize: attachment.file.size,
        })
        const filePath = `messageAttachments/${conversationId}/${Date.now()}_${attachment.file.name}`
        const fileRef = ref(storage, filePath)
        await uploadBytes(fileRef, attachment.file)
        attachmentUrl = await getDownloadURL(fileRef)
        attachmentType = attachment.type
        attachmentName = attachment.file.name
        console.log('Attachment uploaded successfully:', {
          attachmentUrl,
          attachmentType,
          attachmentName,
        })
      } catch (uploadError) {
        console.error('Error uploading attachment:', uploadError)
        // Continue with message sending even if attachment upload fails
      }
    }
    // Prepare message data with defaults for any missing fields
    const messageData = {
      senderId,
      receiverId,
      text: text || '',
      timestamp: serverTimestamp(),
      read: false,
      createdAt: serverTimestamp(),
      type: attachment ? attachment.type : 'text',
    }
    // Only add attachment fields if they exist
    if (attachmentUrl) {
      messageData['attachmentUrl'] = attachmentUrl
      messageData['attachmentType'] = attachmentType
      messageData['attachmentName'] = attachmentName
    }
    console.log('Creating new message with data:', messageData)
    // Use subcollection path for messages
    const messagesRef = collection(
      db,
      'conversations',
      conversationId,
      'messages',
    )
    // Explicitly verify the collection path is correct
    console.log(
      `Using collection path: conversations/${conversationId}/messages`,
    )
    try {
      const newMessage = await addDoc(messagesRef, messageData)
      console.log(
        `Message created with ID: ${newMessage.id} in subcollection of conversation ${conversationId}`,
      )
      // Determine last message text for conversation
      let lastMessageText = text || ''
      if (!lastMessageText && attachmentName) {
        lastMessageText = `Sent ${attachmentType || 'file'}: ${attachmentName}`
      } else if (!lastMessageText && attachmentType) {
        lastMessageText = `Sent ${attachmentType}`
      } else if (!lastMessageText) {
        lastMessageText = 'Sent a message'
      }
      // Update conversation with last message info
      const updateData = {
        lastMessage: lastMessageText,
        lastMessageTimestamp: serverTimestamp(),
        lastMessageSenderId: senderId,
        updatedAt: serverTimestamp(),
      }
      // Only mark as unread if the message is not from the receiver
      if (senderId !== receiverId) {
        updateData['unread'] = true
      }
      console.log(
        `Updating conversation ${conversationId} with data:`,
        updateData,
      )
      await updateDoc(conversationRef, updateData)
      return newMessage.id
    } catch (docError) {
      console.error(
        `Error creating message document in subcollection:`,
        docError,
      )
      if (docError instanceof Error) {
        console.error('Error details:', docError.message)
        console.error('Error stack:', docError.stack)
      }
      throw new Error(
        `Failed to create message: ${docError.message || 'Unknown error'}`,
      )
    }
  } catch (error) {
    console.error('Error sending message:', error)
    if (error instanceof Error) {
      console.error('Error details:', error.message)
      console.error('Error stack:', error.stack)
    }
    throw error
  }
}

// Get all messages in a conversation
export const getMessages = async (
  conversationId: string,
): Promise<Message[]> => {
  try {
    // Use subcollection path for messages
    const messagesRef = collection(
      db,
      'conversations',
      conversationId,
      'messages',
    )
    const q = query(messagesRef, orderBy('timestamp', 'asc'))
    const querySnapshot = await getDocs(q)
    console.log(
      `Retrieved ${querySnapshot.docs.length} messages from conversation ${conversationId}`,
    )
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      conversationId, // Add this since it's not stored in the document
      ...doc.data(),
    })) as Message[]
  } catch (error) {
    console.error(
      `Error getting messages for conversation ${conversationId}:`,
      error,
    )
    throw error
  }
}

// Get all conversations for a user (teacher or student)
export const getUserConversations = async (
  userId: string,
): Promise<Conversation[]> => {
  try {
    if (!userId) {
      console.error('getUserConversations called with empty userId')
      return []
    }
    console.log(`[P1 FIX] Getting conversations with limit for user: ${userId}`)
    const conversationsRef = collection(db, 'conversations')
    // Query for conversations where user is either teacher or student
    const teacherQuery = query(
      conversationsRef,
      where('teacherId', '==', userId),
      orderBy('lastMessageTimestamp', 'desc'),
      limit(CONVERSATION_QUERY_LIMIT), // ✅ CRITICAL: Limit query results
    )
    const studentQuery = query(
      conversationsRef,
      where('studentId', '==', userId),
      orderBy('lastMessageTimestamp', 'desc'),
      limit(CONVERSATION_QUERY_LIMIT), // ✅ CRITICAL: Limit query results
    )
    const teacherSnapshot = await getDocs(teacherQuery)
    const studentSnapshot = await getDocs(studentQuery)
    console.log(
      `[P1 FIX] Found ${teacherSnapshot.docs.length} conversations as teacher (limit: ${CONVERSATION_QUERY_LIMIT})`,
    )
    console.log(
      `[P1 FIX] Found ${studentSnapshot.docs.length} conversations as student (limit: ${CONVERSATION_QUERY_LIMIT})`,
    )
    // Map conversations and add validation for required fields
    const teacherConversations = teacherSnapshot.docs.map((doc) => {
      const data = doc.data()
      // Ensure all required fields have default values if missing
      return {
        id: doc.id,
        teacherId: data.teacherId || userId,
        teacherName: data.teacherName || 'Teacher',
        teacherAvatar: data.teacherAvatar || '',
        studentId: data.studentId || '',
        studentName: data.studentName || 'Student',
        studentAvatar: data.studentAvatar || '',
        lastMessage: data.lastMessage || '',
        lastMessageTimestamp: data.lastMessageTimestamp || new Date(),
        lastMessageSenderId: data.lastMessageSenderId || '',
        unread: !!data.unread,
        instrument: data.instrument || 'Music',
      } as Conversation
    })
    const studentConversations = studentSnapshot.docs.map((doc) => {
      const data = doc.data()
      // Ensure all required fields have default values if missing
      return {
        id: doc.id,
        teacherId: data.teacherId || '',
        teacherName: data.teacherName || 'Teacher',
        teacherAvatar: data.teacherAvatar || '',
        studentId: data.studentId || userId,
        studentName: data.studentName || 'Student',
        studentAvatar: data.studentAvatar || '',
        lastMessage: data.lastMessage || '',
        lastMessageTimestamp: data.lastMessageTimestamp || new Date(),
        lastMessageSenderId: data.lastMessageSenderId || '',
        unread: !!data.unread,
        instrument: data.instrument || 'Music',
      } as Conversation
    })
    // Filter out invalid conversations (missing teacherId or studentId)
    const validTeacherConversations = teacherConversations.filter(
      (conv) => !!conv.studentId,
    )
    const validStudentConversations = studentConversations.filter(
      (conv) => !!conv.teacherId,
    )
    if (validTeacherConversations.length < teacherConversations.length) {
      console.warn(
        `Filtered out ${teacherConversations.length - validTeacherConversations.length} invalid teacher conversations`,
      )
    }
    if (validStudentConversations.length < studentConversations.length) {
      console.warn(
        `Filtered out ${studentConversations.length - validStudentConversations.length} invalid student conversations`,
      )
    }
    // Combine and sort by timestamp
    const allConversations = [
      ...validTeacherConversations,
      ...validStudentConversations,
    ]
    // IMPROVED: More robust timestamp sorting logic
    return allConversations.sort((a, b) => {
      // Get timestamp values, properly handling Firestore timestamps
      const getTimestampValue = (timestamp: any): number => {
        // For Firestore timestamps with toDate method
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime()
          } catch (e) {
            console.error('Error converting Firestore timestamp:', e)
            return 0
          }
        }
        // For Date objects
        if (timestamp instanceof Date) {
          return timestamp.getTime()
        }
        // For numeric timestamps
        if (typeof timestamp === 'number') {
          return timestamp
        }
        // Default case - use 0 to ensure consistent sorting
        return 0
      }
      const timeA = getTimestampValue(a.lastMessageTimestamp)
      const timeB = getTimestampValue(b.lastMessageTimestamp)
      // Sort in descending order (newest first)
      return timeB - timeA
    })
  } catch (error) {
    console.error('Error getting user conversations:', error)
    return []
  }
}

// Set up real-time listener for user conversations
export const subscribeToUserConversations = (
  userId: string,
  callback: (conversations: Conversation[]) => void,
) => {
  if (!userId) {
    console.error('subscribeToUserConversations called with empty userId')
    callback([])
    return () => {}
  }
  console.log(
    `[Messaging] Setting up dual conversation subscription for user: ${userId}`,
  )

  // Create queries for both teacher and student roles
  const conversationsRef = collection(db, 'conversations')
  const teacherQuery = query(
    conversationsRef,
    where('teacherId', '==', userId),
    orderBy('lastMessageTimestamp', 'desc'),
  )
  const studentQuery = query(
    conversationsRef,
    where('studentId', '==', userId),
    orderBy('lastMessageTimestamp', 'desc'),
  )

  // Shared state to combine results from both listeners
  let teacherConversations: Conversation[] = []
  let studentConversations: Conversation[] = []
  let teacherReady = false
  let studentReady = false

  // Helper to combine, deduplicate, sort, and emit
  // Emits as soon as ANY listener has fired — does NOT wait for both
  const emitCombined = () => {
    // Emit if at least one listener has fired
    if (!teacherReady && !studentReady) return

    // Deduplicate by conversation ID (in case a user is both teacher and student)
    const seen = new Set<string>()
    const allConversations: Conversation[] = []
    for (const conv of [...teacherConversations, ...studentConversations]) {
      if (!seen.has(conv.id)) {
        seen.add(conv.id)
        allConversations.push(conv)
      }
    }

    // Sort by lastMessageTimestamp descending
    allConversations.sort((a, b) => {
      const getTimestampValue = (timestamp: any): number => {
        if (timestamp && typeof timestamp === 'object' && timestamp.toDate) {
          try {
            return timestamp.toDate().getTime()
          } catch (e) {
            return 0
          }
        }
        if (timestamp instanceof Date) return timestamp.getTime()
        if (typeof timestamp === 'number') return timestamp
        return 0
      }
      return (
        getTimestampValue(b.lastMessageTimestamp) -
        getTimestampValue(a.lastMessageTimestamp)
      )
    })

    console.log(
      `[Messaging] Emitting ${allConversations.length} combined conversations (${teacherConversations.length} as teacher, ${studentConversations.length} as student, teacherReady=${teacherReady}, studentReady=${studentReady})`,
    )
    callback(allConversations)
  }

  // Helper to map snapshot docs to Conversation objects
  const mapConversations = (
    docs: any[],
    fallbackRole: 'teacher' | 'student',
  ): Conversation[] => {
    return docs
      .map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          teacherId:
            data.teacherId || (fallbackRole === 'teacher' ? userId : ''),
          teacherName: data.teacherName || 'Teacher',
          teacherAvatar: data.teacherAvatar || '',
          studentId:
            data.studentId || (fallbackRole === 'student' ? userId : ''),
          studentName: data.studentName || 'Student',
          studentAvatar: data.studentAvatar || '',
          lastMessage: data.lastMessage || '',
          lastMessageTimestamp: data.lastMessageTimestamp || new Date(),
          lastMessageSenderId: data.lastMessageSenderId || '',
          unread: !!data.unread,
          instrument: data.instrument || 'Music',
        } as Conversation
      })
      .filter((conv) => !!conv.teacherId && !!conv.studentId)
  }

  // Listener 1: Conversations where user is the teacher
  const teacherUnsubscribe = onSnapshot(
    teacherQuery,
    (snapshot) => {
      console.log(
        `[Messaging] Real-time update: ${snapshot.docs.length} conversations as teacher`,
      )
      teacherConversations = mapConversations(snapshot.docs, 'teacher')
      teacherReady = true
      emitCombined()
    },
    (error) => {
      console.error(
        '[Messaging] Error in teacher conversations subscription:',
        error,
      )
      // Do NOT clear teacherConversations — preserve any previously loaded data
      // Mark as ready and always emit so the UI can show whatever data we have
      teacherReady = true
      emitCombined()
    },
  )

  // Listener 2: Conversations where user is the student
  const studentUnsubscribe = onSnapshot(
    studentQuery,
    (snapshot) => {
      console.log(
        `[Messaging] Real-time update: ${snapshot.docs.length} conversations as student`,
      )
      studentConversations = mapConversations(snapshot.docs, 'student')
      studentReady = true
      emitCombined()
    },
    (error) => {
      console.error(
        '[Messaging] Error in student conversations subscription:',
        error,
      )
      // Do NOT clear studentConversations — preserve any previously loaded data
      // Mark as ready and always emit so the UI can show whatever data we have
      studentReady = true
      emitCombined()
    },
  )

  // Return a function to unsubscribe from both listeners
  return () => {
    console.log('[Messaging] Cleaning up dual conversation subscriptions')
    teacherUnsubscribe()
    studentUnsubscribe()
  }
}

// Set up real-time listener for new messages in a conversation
export const subscribeToConversationMessages = (
  conversationId: string,
  callback: (messages: Message[]) => void,
) => {
  if (!conversationId) {
    console.error(
      'subscribeToConversationMessages called with empty conversationId',
    )
    callback([])
    return () => {}
  }
  console.log(
    `🔥 REALTIME: Setting up message subscription for conversation: ${conversationId}`,
  )
  // Use subcollection path for messages
  const messagesRef = collection(
    db,
    'conversations',
    conversationId,
    'messages',
  )
  // Explicitly verify the collection path is correct
  console.log(
    `🔥 REALTIME: Listening to collection path: conversations/${conversationId}/messages`,
  )
  // Create a query sorted by timestamp
  const q = query(messagesRef, orderBy('timestamp', 'asc'))
  try {
    // Set up real-time listener with onSnapshot
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        console.log(
          `🔥 REALTIME: Received ${snapshot.docs.length} messages in real-time update for conversation ${conversationId}`,
        )
        // Log any document changes for debugging
        snapshot.docChanges().forEach((change) => {
          console.log(
            `🔥 REALTIME: Document ${change.doc.id} ${change.type} in conversation ${conversationId}:`,
            change.doc.data(),
          )
        })
        // Map documents to Message objects with proper validation
        const messages = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            conversationId: conversationId,
            senderId: data.senderId || '',
            receiverId: data.receiverId || '',
            text: data.text || '',
            timestamp: data.timestamp || new Date(),
            read: !!data.read,
            attachmentUrl: data.attachmentUrl,
            attachmentType: data.attachmentType,
            attachmentName: data.attachmentName,
          } as Message
        })
        console.log(
          `🔥 REALTIME: Calling callback with ${messages.length} messages for conversation ${conversationId}`,
        )
        callback(messages)
      },
      (error) => {
        console.error(
          `Error in messages subscription for conversation ${conversationId}:`,
          error,
        )
        // Do NOT clear messages on error — preserve any previously loaded messages
      },
    )
    return unsubscribe
  } catch (error) {
    console.error(
      `Error setting up message listener for ${conversationId}:`,
      error,
    )
    callback([])
    return () => {}
  }
}

// Mark messages as read
export const markMessagesAsRead = async (
  conversationId: string,
  userId: string,
) => {
  try {
    // FIXED: Use subcollection instead of top-level collection
    const messagesRef = collection(
      db,
      'conversations',
      conversationId,
      'messages',
    )
    const q = query(
      messagesRef,
      where('receiverId', '==', userId),
      where('read', '==', false),
    )
    const querySnapshot = await getDocs(q)
    console.log(
      `Marking ${querySnapshot.docs.length} messages as read in conversation ${conversationId}`,
    )
    // Update each message
    const promises = querySnapshot.docs.map((doc) => {
      return updateDoc(doc.ref, { read: true })
    })
    // Reset unread flag in conversation
    const conversationRef = doc(db, 'conversations', conversationId)
    promises.push(updateDoc(conversationRef, { unread: false }))
    await Promise.all(promises)
  } catch (error) {
    console.error(
      `Error marking messages as read in conversation ${conversationId}:`,
      error,
    )
    throw error
  }
}

// Get teacher by ID
export const getTeacherById = async (teacherId: string) => {
  try {
    const teacherRef = doc(db, 'teachers', teacherId)
    const teacherDoc = await getDoc(teacherRef)
    if (!teacherDoc.exists()) {
      return null
    }
    return {
      id: teacherDoc.id,
      ...teacherDoc.data(),
    }
  } catch (error) {
    console.error('Error getting teacher:', error)
    throw error
  }
}

// Add debug function for checking Firestore conversations
export const debugCheckFirestoreConversations = async (userId: string) => {
  try {
    console.group('🔍 DIAGNOSTIC: Firestore Conversations Check')
    // Check conversations where user is teacher
    const userConversations = await getUserConversations(userId)
    console.log('Found conversations for user:', userConversations.length)
    // Log each conversation
    userConversations.forEach((conv, index) => {
      console.log(`Conversation ${index + 1}:`, {
        id: conv.id,
        teacherName: conv.teacherName,
        studentName: conv.studentName,
        lastMessage: conv.lastMessage,
        timestamp: conv.lastMessageTimestamp,
      })
    })
    console.groupEnd()
    return userConversations
  } catch (error) {
    console.error(
      '🔍 DIAGNOSTIC: Error checking Firestore conversations:',
      error,
    )
    console.groupEnd()
    throw error
  }
}
